<template>
  <div class="customer-page d-flex flex-column">
    <header
      v-if="showHeader()"
      id="customer-header"
      class="customer-header d-flex justify-content-between align-items-center i-px-11 i-py-8"
    >
      <div class="left-side d-flex flex-row">
        <loading-boxes
          v-if="!selectedCustomer"
          :config="{ showAvatar: false, lengths: [200, 150], repeat: [0] }"
        />
        <div v-else>
          <div class="i-text-button-2 i-mb-2 customer-title">
            <div class="d-flex flex-row">
              <i-inline-input
                :value="selectedCustomer.name"
                data-testid="customer-name-input"
                class="customer-name-input i-mr-2 i-text-neutral-120"
                placeholder="Customer name"
                @input="selectedCustomer.name = $event.target.value"
                @debounced-input="updateCustomerName"
              />
              <customer-fraud-risk-badge
                v-if="
                  selectedCustomer.risk_level && !canViewBusinessComplianceAgent
                "
                :customer="selectedCustomer"
              />
            </div>
          </div>

          <div
            class="customer-subtitle i-mt-1 i-text-subheading-3 d-flex i-text-neutral-110"
          >
            <div class="d-flex flow-row align-items-center">
              <i-icon
                symbol="small-calendar-date"
                :size="12"
                class="i-text-neutral-90"
              ></i-icon>
              <span class="i-ml-4 i-mr-8"
                ><span class="created-label i-mr-2">Created</span
                >{{ formatDateTime(selectedCustomer.created_at, 'lll') }}</span
              >
            </div>
            <div class="d-flex flow-row align-items-center">
              <i-icon
                symbol="small-user-edit"
                :size="12"
                class="i-text-neutral-90"
              ></i-icon>
              <span class="i-ml-4">{{ customerCreatorEmail }}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="selectedCustomer"
        data-testid="customer-header-actions"
        class="right-side d-flex align-items-center justify-content-end i-gap-2"
      >
        <i-button
          v-if="isPossibleToAddToBlocklist"
          id="customer-blocklist-trigger"
          v-read-only-check
          :disabled="showBlocklistSuggestions"
          class="blocklist-button"
          left-icon="medium-user-block"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          @button-click="onClickShowBlocklist"
        ></i-button>
        <i-tooltip
          title="Add to Blocklist"
          target="#customer-blocklist-trigger"
          :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
        ></i-tooltip>
        <i-button
          v-if="isPdfExportEnabled"
          id="customer-export-button"
          v-read-only-check
          :disabled="!isCompletePdfExport"
          class="export-button"
          left-icon="medium_download_1"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          @button-click="exportPdf"
        ></i-button>
        <i-tooltip
          v-if="isPdfExportEnabled"
          title="Export"
          target="#customer-export-button"
          :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
        ></i-tooltip>
        <i-button
          id="customer-delete-button"
          data-testid="delete-customer-button"
          v-read-only-check
          class="delete-customer-button i-mr-6"
          left-icon="medium-delete-bin-1"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          @button-click="deleteCustomer"
        ></i-button>
        <i-tooltip
          title="Delete"
          target="#customer-delete-button"
          :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
        ></i-tooltip>
        <customer-approval-buttons
          :customer="selectedCustomer"
          :grouped-parsed-details="groupedParsedDetails"
          :show="showBlocklistSuggestions"
        />
        <customer-blocklist-suggestions
          v-if="selectedCustomer.parsedDetails"
          :customer="selectedCustomer"
          :grouped-parsed-details="groupedParsedDetails"
          :show="showBlocklistSuggestions"
          @input-text="
            (index, type, value) => (groupedParsedDetails[index][type] = value)
          "
        />
        <chat-toggle-button v-if="isChatAgentEnabled" class="i-ml-6" />
      </div>
    </header>
    <customer-page-tabs v-if="creditInsightsTabsEnabled"></customer-page-tabs>
    <router-view></router-view>
    <chat-floating-prompt-box v-if="isChatAgentEnabled" />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import CustomerBlocklistSuggestions from '@/V2/customers/CustomerBlocklistSuggestions';
import CustomerPageTabs from '@/V2/customers/CustomerPageTabs';
import * as parsedDetailsTypes from '@/constants/parsedDetailsTypes';
import parsedDetailsConfig from '@/constants/parsedDetailsConfig';
import CustomerApprovalButtons from '@/V2/customers/CustomerApprovalButtons';
import {
  iIcon,
  iButton,
  iInlineInput,
  showToast,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
  iTooltipAlignmentMode,
  iTooltip,
} from '@inscribe/signal';
import { formatDateTime } from '@/utils/date';
import { InscribeEventsLogger, INSCRIBE_EVENTS } from '@/events-logger';
import CustomerFraudRiskBadge from '@/V2/customers/fraud/CustomerFraudRiskBadge.vue';
import ChatToggleButton from '@/V2/chat/ChatToggleButton.vue';
import LoadingBoxes from '@/V2/components/LoadingBoxes.vue';
import ChatFloatingPromptBox from '@/V2/chat/ChatFloatingPromptBox.vue';

export default {
  components: {
    'customer-approval-buttons': CustomerApprovalButtons,
    'customer-blocklist-suggestions': CustomerBlocklistSuggestions,
    'customer-page-tabs': CustomerPageTabs,
    iIcon,
    iButton,
    iInlineInput,
    iTooltip,
    CustomerFraudRiskBadge,
    ChatToggleButton,
    LoadingBoxes,
    ChatFloatingPromptBox,
  },
  props: {
    isPdfExportEnabled: {
      type: Boolean,
      default: false,
    },
    isCompletePdfExport: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      historyOpen: false,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
      iTooltipAlignmentMode,
    };
  },
  computed: {
    ...mapGetters([
      'creditInsightsTabsEnabled',
      'canViewAIFraudAnalyst',
      'canViewBusinessComplianceAgent',
      'isChatAgentEnabled',
    ]),
    ...mapState({
      selectedCustomer: (state) => state.customers.selectedCustomer,
      groupedParsedDetails: (state) => state.customers.groupedParsedDetails,
      showBlocklistSuggestions: (state) =>
        state.customers.showBlocklistSuggestions,
    }),
    customerCreatorEmail() {
      return this.selectedCustomer?.creator?.email || '';
    },
    isPossibleToAddToBlocklist() {
      // We require name and address to be present for uniqueness
      return this.groupedParsedDetails && this.groupedParsedDetails.length > 0;
    },
  },
  watch: {
    selectedCustomer: {
      immediate: true,
      handler: function (newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.getCustomerParsedDetails(newValue.id)
            .then(({ data }) => {
              newValue.parsedDetails = data.data;
              const parsedDetails = this.getGroupedParsedDetails(data.data);
              this.setGroupedParsedDetails(parsedDetails);
            })
            .catch((err) => {
              showToast({
                title: 'Error fetching customer parsed details',
                text: err?.response?.data?.message,
                isError: true,
              });
            });
        }
      },
    },
  },
  methods: {
    ...mapActions(['getCustomerParsedDetails', 'fetchAndCacheCustomer']),
    ...mapMutations(['setShowBlocklistSuggestions', 'setGroupedParsedDetails']),
    exportPdf() {
      this.$emit('get-pdf-report');
      InscribeEventsLogger.logEvent(INSCRIBE_EVENTS.CUSTOMER_CTA_CLICKED, {
        itemName: 'export_pdf',
      });
    },
    deleteCustomer() {
      this.$emit('delete-customer');
      InscribeEventsLogger.logEvent(INSCRIBE_EVENTS.CUSTOMER_CTA_CLICKED, {
        itemName: 'delete_customer',
      });
    },
    isFromAnalytics() {
      return this.$route.query.return_to === 'analytics';
    },
    formatDateTime,
    getGroupedParsedDetails(parsedDetails) {
      const { show: showAddressesFn } =
        parsedDetailsConfig[parsedDetailsTypes.ADDRESS];

      const addresses = parsedDetails.filter(
        ({ instances, type }) =>
          type === parsedDetailsTypes.ADDRESS &&
          instances.some(showAddressesFn),
      );

      const { show: showNameFn } = parsedDetailsConfig[parsedDetailsTypes.NAME];
      return parsedDetails
        .filter(
          ({ instances, type }) =>
            type === parsedDetailsTypes.NAME && instances.some(showNameFn),
        )
        .reduce((acc, parsedName) => {
          const documentIds = parsedName.instances.map(
            ({ document_id }) => document_id,
          );
          const matchingAddress = addresses.find(({ instances }) =>
            instances.find((addressInstance) =>
              documentIds.includes(addressInstance.document_id),
            ),
          );
          if (matchingAddress) {
            return [
              ...acc,
              {
                name: parsedName.normalized,
                address: matchingAddress.normalized,
              },
            ];
          }
          return acc;
        }, []);
    },
    updateCustomerName() {
      // DEPRECATED code below which is still used in this component
      if (this.selectedCustomer.name.length > 0) {
        this.$store.dispatch('updateCustomer', this.selectedCustomer).then(
          (response) => {},
          ({ response: { data, status } }) => {
            showToast({
              title: 'Error changing customer name',
              text:
                status === 403
                  ? 'You do not have permission to change the customer name'
                  : data.message,
              isError: true,
            });
          },
        );
      }
      // Refetch and cache the customer in CustomersStore which is the
      // preferred data flow moving forward
      this.fetchAndCacheCustomer(this.selectedCustomer.id);
    },
    onClickShowBlocklist() {
      this.setShowBlocklistSuggestions(true);
      InscribeEventsLogger.logEvent(INSCRIBE_EVENTS.CUSTOMER_CTA_CLICKED, {
        itemName: 'blocklist',
      });
    },
    showHeader() {
      return this.$route.name !== 'customer-insights';
    },
  },
};
</script>

<style lang="scss">
/* global styles */
.redesign-customer-view-body {
  overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
.customer-page {
  height: 100%;

  .customer-tabs {
    display: none;

    @media only screen and (min-width: 768px) and (max-width: 900px) {
      display: block;
    }
  }

  header {
    background-color: $light-neutral-default;
    box-shadow: $box-shadow-default;
    z-index: 50;
    min-height: 76px;

    @media only screen and (min-width: 768px) and (max-width: 900px) {
      box-shadow: none;
    }

    .left-side {
      align-items: center;

      .customer-title {
        line-height: 18px;
      }

      .customer-subtitle {
        align-items: center;
        color: $light-neutral-110;
        flex-direction: row;

        @include respond-to('medium') {
          flex-direction: column;
          align-items: flex-start;

          .created-label {
            display: none;
          }
        }
      }

      .customer-name-input :deep(input) {
        @extend .i-text-neutral-120;
        @extend .i-text-heading-5;
      }
    }
  }

  :deep(.list-groups-container) {
    $border-radius: 20px;

    .list-group {
      background: var(--surface-neutral);
      gap: 1px;

      .title {
        color: var(--text-neutral);
        background: var(--base);
        border-top-left-radius: $border-radius;
        border-top-right-radius: $border-radius;
      }

      .items-list {
        gap: 1px;
        border-bottom-left-radius: $border-radius;
        border-bottom-right-radius: $border-radius;

        .item-card {
          border-radius: 0;

          &:last-child {
            border-bottom-left-radius: $border-radius;
            border-bottom-right-radius: $border-radius;
          }
        }
      }
    }
  }
}
</style>
